import React, {
    FunctionComponent,
    MouseEventHandler,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { MicroBlocked } from '@yandex-360/iconostasis/dist/icons/MicroBlocked';
import { MicroOff } from '@yandex-360/iconostasis/dist/icons/MicroOff';
import { MicroOn } from '@yandex-360/iconostasis/dist/icons/MicroOn';
import { Event as CustomEvent } from '@yandex-int/messenger.utils';
import cn from 'classnames';
import _noop from 'lodash/noop';
import { useSelector } from 'react-redux';

import { ModalKickConfirm } from '../../../../shared/components/modal-kick-confirm';
import { ModalStopRecordingConfirm } from '../../../../shared/components/modal-stop-recording-confirm';
import ModerationPopup from '../../../../shared/components/moderation-popup';
import ParticipantIcon from '../../../../shared/components/participant-icon';
import { PngImage } from '../../../../shared/components/png-image';
import SvgImage from '../../../../shared/components/svg-image';
import { SELF } from '../../../../shared/constants';
import { useModerationPermissionsExtended } from '../../../../shared/features/moderation/hooks/use-moderation-permissions-extended';
import useModerationParticipantAudio from '../../../../shared/features/moderation/hooks/useModerationParticipantAudio';
import useShouldShowModerationControls from '../../../../shared/features/moderation/hooks/useShouldShowModerationControls';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { isSelfModerator } from '../../selectors';
import { Participant, ParticipantID } from '../../types';
import ModerationControlButton from '../moderation-control-button';
import css from './participant.css';

interface ParticipantProps {
    participant: Participant;
    showRecordingIcon?: boolean;
    showBroadcastIcon?: boolean;
    showModerationPopup?: boolean;
    moderationPopupScope?: RefObject<HTMLDivElement>;
    openModerationPopup?: () => void;
    closeModerationPopup?: () => void;
    isMobile?: boolean;
    onParticipantListChanged: CustomEvent;
}

const ParticipantComponent: FunctionComponent<ParticipantProps> = ({
    participant,
    showRecordingIcon,
    showBroadcastIcon,
    showModerationPopup,
    moderationPopupScope,
    openModerationPopup = _noop,
    closeModerationPopup = _noop,
    isMobile,
    onParticipantListChanged,
}) => {
    const selfModerator = useSelector(isSelfModerator);
    const { id, displayName, audioTrackState, isModerator } = participant;
    const { disableMicrophoneButton, muteParticipant, isMutedTrack, hasMicrophonePermission } =
        useModerationParticipantAudio(id);
    const { isPermissionsExtendedEnabled, hasAnyModerationPermission } = useModerationPermissionsExtended();
    const { shouldShowModerationPopupButton, shouldShowModerationMicrophone } = useShouldShowModerationControls(
        id,
        isMobile
    );

    const onMuteParticipantClick = useCallback<MouseEventHandler>(
        (event) => {
            countMetrika('meeting_screen', 'participants_list', 'mute_participant');
            muteParticipant(event);
        },
        [muteParticipant]
    );

    const moderationPopupAnchorRef = useRef<HTMLDivElement>(null);
    const moreButtonRef = useRef<HTMLDivElement>(null);
    const isAudioOn = Boolean(audioTrackState && !audioTrackState.muted);

    const [participantIdToKick, setParticipantIdToKick] = useState<string | undefined>(undefined);
    const handleShowModalKickConfirm = useCallback((participantId: ParticipantID) => {
        setParticipantIdToKick(participantId);
    }, []);
    const handleCloseModalKickConfirm = useCallback(() => setParticipantIdToKick(''), []);

    const [participantIdToStopRecording, setParticipantIdToStopRecording] = useState<ParticipantID | 'all' | null>(
        null
    );
    const handleShowModalStopRecording = useCallback((participantId: ParticipantID | 'all') => {
        setParticipantIdToStopRecording(participantId);
    }, []);
    const handleCloseModalStopRecordingConfirm = useCallback(() => setParticipantIdToStopRecording(null), []);

    const onParticipantClick =
        isMobile && (selfModerator || (isPermissionsExtendedEnabled && hasAnyModerationPermission))
            ? openModerationPopup
            : _noop;
    const onPopupClose = useCallback(
        (event?: Event) => {
            event?.stopPropagation?.();
            closeModerationPopup();
        },
        [closeModerationPopup]
    );

    const reactionType = participant.reaction?.reactionType;

    useEffect(() => {
        if (reactionType === 'hand') {
            countMetrika('meeting_screen', 'participants_list', 'hand_participants');
        }
    }, [reactionType]);

    useEffect(() => {
        if (showModerationPopup) {
            countMetrika('meeting_screen', 'participants_list', 'participant_more_button');
        }
    }, [showModerationPopup]);

    useEffect(() => {
        if (showBroadcastIcon) {
            countMetrika('meeting_screen', 'participants_list', 'translation_label');
        }
    }, [showBroadcastIcon]);

    useEffect(() => {
        if (showRecordingIcon) {
            countMetrika('meeting_screen', 'participants_list', 'record_label');
        }
    }, [showRecordingIcon]);

    const MicrophoneIcon = (() => {
        if (isPermissionsExtendedEnabled && !hasMicrophonePermission) {
            return MicroBlocked;
        }
        return isMutedTrack ? MicroOff : MicroOn;
    })();
    const microphoneIcon = <MicrophoneIcon variant="solid" size="sm" />;

    return (
        <div
            className={cn(css.Participant, {
                [css.IsModerationPopupOpen]: showModerationPopup,
                [css.CanModerate]: shouldShowModerationMicrophone,
            })}
        >
            {/* Этот элемент нужен только для ref. Если добавть ref корневому элементу, то попап слетает при скролле */}
            <div onClick={onParticipantClick} className={css.ModerationPopupAnchor} ref={moderationPopupAnchorRef} />
            <div className={css.ParticipantData}>
                <ParticipantIcon participantId={id} width={40} height={40} />
                <div className={cn(css.ParticipantTextData)}>
                    <span className={css.DisplayName}>{displayName}</span>
                    {isModerator && (
                        <span className={css.ParticipantRole}>
                            {participant.isOwner ? i18n('moderation', 'host') : i18n('moderation', 'co-host')}
                        </span>
                    )}
                </div>
            </div>
            <div className={css.ParticipantStatusBlock}>
                {reactionType && <PngImage name={reactionType} width={16} height={16} />}
                {showBroadcastIcon && <SvgImage className={css.BroadcastIcon} imageName="broadcastEnabled" />}
                {showRecordingIcon && <SvgImage className={css.RecordingIcon} imageName="recording" />}
                {id !== SELF && shouldShowModerationPopupButton && (
                    <ModerationControlButton
                        innerRef={moreButtonRef}
                        className={cn(css.ModerationItem, css.ModerationItemMore)}
                        iconClassName={css.ModerationItemMoreIcon}
                        iconName="more"
                        onClick={openModerationPopup}
                        title={i18n('toolbar', 'more-popup-alt-title')}
                        testId="more-popup-alt-button"
                    />
                )}

                {shouldShowModerationMicrophone && (
                    <ModerationControlButton
                        className={cn(css.ModerationItem, css.ModerationItemMicrophone)}
                        iconClassName={css.ModerationItemIcon}
                        icon={microphoneIcon}
                        disabled={disableMicrophoneButton}
                        onClick={onMuteParticipantClick}
                        testId={isAudioOn ? 'turn-on-mic-button' : 'turn-off-mic-button'}
                        title={isAudioOn ? i18n('toolbar', 'turn-off-mic') : i18n('toolbar', 'turn-on-mic')}
                    />
                )}
                {audioTrackState?.muted && (
                    <div
                        className={cn(css.Mute, {
                            [css.disabled]: disableMicrophoneButton,
                        })}
                        role="button"
                        aria-label={i18n('toolbar', 'turn-on-mic')}
                    >
                        {microphoneIcon}
                    </div>
                )}
            </div>
            <div className={css.ModerationItems}>
                {id !== SELF && showModerationPopup && (
                    <ModerationPopup
                        participantId={id}
                        placement={'bottom left'}
                        offset={4}
                        triggerRef={moderationPopupAnchorRef}
                        scope={moderationPopupScope}
                        onClose={onPopupClose}
                        onKickConfirm={handleShowModalKickConfirm}
                        onStopRecordingConfirm={handleShowModalStopRecording}
                        isMobile={isMobile}
                        onUpdate={onParticipantListChanged}
                        isRecordingActive={showRecordingIcon}
                    />
                )}
                {participantIdToKick ? (
                    <ModalKickConfirm onClose={handleCloseModalKickConfirm} participantId={participantIdToKick} />
                ) : null}
                {participantIdToStopRecording ? (
                    <ModalStopRecordingConfirm
                        participantId={participantIdToStopRecording}
                        onClose={handleCloseModalStopRecordingConfirm}
                    />
                ) : null}
            </div>
        </div>
    );
};

export { ParticipantComponent };
